import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { BookOpen, FileQuestion, ClipboardList, LogOut, Globe, Menu, X, Video, BarChart3, Users, Database, GraduationCap, Radio, FileText, MessageSquare, Award } from 'lucide-react';

// Teacher Pages
import QuestionBank from '../components/teacher/QuestionBank';
import ExamManagement from '../components/teacher/ExamManagement';
import TeacherHome from '../components/teacher/TeacherHome';
import AdminPanel from '../components/teacher/AdminPanel';
import ResultsAnalysis from '../components/teacher/ResultsAnalysis';
import TraineeManagement from '../components/teacher/TraineeManagement';
import DataManagement from '../components/teacher/DataManagement';
import CoursesManagement from '../components/teacher/CoursesManagement';
import LiveStreamManagement from '../components/teacher/LiveStreamManagement';
import VideoDashboard from '../components/teacher/VideoDashboard';
import NotificationBell from '../components/teacher/NotificationBell';
import Reports from '../components/teacher/Reports';
import CertificateManagement from '../components/teacher/CertificateManagement';
import SurveyFillPanel from '../components/common/SurveyFillPanel';

const TeacherDashboard = ({ user, onLogout, onToggleLanguage }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Navigation items - organized by category
  const navItems = [
    // Main
    { path: '/teacher', icon: BookOpen, label: t('dashboard'), exact: true },
    // Content Management
    { path: '/teacher/questions', icon: FileQuestion, label: t('questionBank') },
    { path: '/teacher/exams', icon: ClipboardList, label: t('exams') },
    { path: '/teacher/courses', icon: GraduationCap, label: t('courses') },
    // Media & Live
    { path: '/teacher/videos', icon: Video, label: t('videoManagement') },
    { path: '/teacher/live-stream', icon: Radio, label: t('liveStream') },
    // Users & Analysis
    { path: '/teacher/trainees', icon: Users, label: t('traineeManagement') },
    { path: '/teacher/results-analysis', icon: BarChart3, label: t('resultsAnalysis') },
    { path: '/teacher/reports', icon: FileText, label: t('reports') },
    { path: '/teacher/certificates', icon: Award, label: isRTL ? 'الشهادات' : 'Certificates' },
    { path: '/teacher/surveys', icon: MessageSquare, label: t('surveys') || 'Surveys' },
    // System
    { path: '/teacher/data-management', icon: Database, label: t('dataManagement') },
    { path: '/teacher/admin', icon: Menu, label: t('adminPanel') }
  ];

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                data-testid="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <img src="/logo.png" alt={t('appFullName')} className="h-10 w-10 object-contain" />
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-sm text-gray-600">
                <span className="font-medium">{user.name}</span>
                <span className="mx-2">•</span>
                <span className="text-indigo-600">{t('teacher')}</span>
              </div>
              <NotificationBell />
              <Button
                data-testid="language-toggle-dashboard"
                onClick={onToggleLanguage}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <Globe className="h-5 w-5" />
              </Button>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="icon"
                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 shadow-lg
            transform transition-transform duration-300 ease-in-out z-40
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <nav className="h-full overflow-y-auto p-4 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all
                    ${active
                      ? 'bg-gradient-to-r from-indigo-600 to-cyan-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-[calc(100vh-4rem)] overflow-auto">
          <Routes>
            <Route path="/" element={<TeacherHome />} />
            <Route path="/questions" element={<QuestionBank />} />
            <Route path="/exams" element={<ExamManagement />} />
            <Route path="/courses" element={<CoursesManagement />} />
            <Route path="/live-stream" element={<LiveStreamManagement />} />
            <Route path="/trainees" element={<TraineeManagement />} />
            <Route path="/videos" element={<VideoDashboard />} />
            <Route path="/results-analysis" element={<ResultsAnalysis />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/certificates" element={<CertificateManagement />} />
            <Route path="/surveys" element={<SurveyFillPanel />} />
            <Route path="/data-management" element={<DataManagement />} />
            <Route path="/admin" element={<AdminPanel />} />
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default TeacherDashboard;